import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';
import UserTrafficSourceAuditService from '../../services/UserTrafficSourceAudit/user-traffic-source-audit.service';
import {
  UserTrafficSourceAuditCreateModel,
  ProfessionModel,
  TrafficSourceModel,
} from '../../models/UserTrafficSourceAudit/user-traffic-source-audit-model';

interface CustomerOption { id: number; userName: string; primaryContact?: string; detail?: string; }
interface AdminOption    { id: number; userName: string; }

interface Props {
  visible: boolean;
  onHide:  () => void;
  onSaved: () => void;
}

const UserTrafficSourceAuditAddDialog: React.FC<Props> = ({ visible, onHide, onSaved }) => {
  const toast = useRef<Toast>(null);

  const [saving, setSaving]           = useState(false);
  const [loading, setLoading]         = useState(false);

  // Master data
  const [professions, setProfessions]     = useState<ProfessionModel[]>([]);
  const [trafficSources, setTrafficSources] = useState<TrafficSourceModel[]>([]);
  const [adminUsers, setAdminUsers]       = useState<AdminOption[]>([]);

  // Filtered autocomplete suggestions
  const [filteredCustomers, setFilteredCustomers] = useState<CustomerOption[]>([]);
  const [filteredProf, setFilteredProf]   = useState<any[]>([]);
  const [filteredTs,   setFilteredTs]     = useState<any[]>([]);

  // Form values
  const [customerValue,  setCustomerValue]  = useState<any>(null); // For AutoComplete
  const [customerId,     setCustomerId]     = useState<number | null>(null);
  
  const [profValue,      setProfValue]      = useState<any>(null); // can be string or object
  const [tsValue,        setTsValue]        = useState<any>(null);
  
  const [isIntroDone,    setIsIntroDone]    = useState<boolean>(false);
  const [adminUserId,    setAdminUserId]    = useState<number | null>(null);
  const [specificIssue,  setSpecificIssue]  = useState<string>('');

  const [resolvedProfId, setResolvedProfId] = useState<number | null>(null);
  const [resolvedTsId,   setResolvedTsId]   = useState<number | null>(null);

  // Load masters when dialog opens
  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    Promise.all([
      UserTrafficSourceAuditService.getProfessions(),
      UserTrafficSourceAuditService.getTrafficSources(),
      UserTrafficSourceAuditService.getAdminUsers()
    ]).then(([p, t, a]) => {
      setProfessions(p);
      setTrafficSources(t);
      setAdminUsers(a as any);
    }).catch(() => {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load master data' });
    }).finally(() => setLoading(false));
  }, [visible]);

  const resetForm = () => {
    setCustomerValue(null);
    setCustomerId(null); 
    setProfValue(null); 
    setTsValue(null);
    setIsIntroDone(false);
    setAdminUserId(null); 
    setSpecificIssue('');
    setResolvedProfId(null); 
    setResolvedTsId(null);
  };

  const handleHide = () => { resetForm(); onHide(); };

  // ── Customer autocomplete ───────────────────────────────────────────────
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  const searchCustomer = async (e: AutoCompleteCompleteEvent) => {
      const q = e.query.toLowerCase().trim();
      if (!q) {
          setFilteredCustomers([]);
          return;
      }
      setSearchingCustomers(true);
      try {
          const res = await UserTrafficSourceAuditService.getCustomers(q);
          const mapped = res.map(cust => {
              const id = cust.id || 0;
              const userName = cust.userName || '';
              const primaryContact = cust.primaryContact || '';
              return {
                  ...cust,
                  id,
                  userName,
                  primaryContact,
                  detail: `${userName}${primaryContact ? `, ${primaryContact}` : ''}`
              };
          });
          setFilteredCustomers(mapped);
      } catch (err) {
          console.error("Error searching customers:", err);
      } finally {
          setSearchingCustomers(false);
      }
  };

  const onCustomerSelect = (e: any) => {
      const val = e.value;
      if (val && typeof val === 'object' && val.id) {
          setCustomerValue(val);
          setCustomerId(val.id);
      }
  };

  const handleAlphanumericKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const char = String.fromCharCode(e.charCode || e.keyCode);
    if (!/^[a-zA-Z0-9\s]$/.test(char) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Tab') {
      e.preventDefault();
    }
  };

  // ── Profession autocomplete ─────────────────────────────────────────────
  const searchProf = (e: AutoCompleteCompleteEvent) => {
    const cleanQuery = e.query.replace(/[^a-zA-Z0-9\s]/g, '');
    const q = cleanQuery.toLowerCase().trim();
    if (!q) {
      setFilteredProf([...professions]);
      return;
    }
    
    const matches = professions.filter(p => p.professionName.toLowerCase().includes(q));
    const isExactMatch = professions.some(p => p.professionName.toLowerCase() === q);
    
    if (!isExactMatch) {
       setFilteredProf([...matches, { id: -1, professionName: `+ Add "${cleanQuery}"`, isActive: true, isNew: true, query: cleanQuery }]);
    } else {
       setFilteredProf(matches);
    }
  };

  const onProfSelect = async (e: any) => {
    const val = e.value;
    if (val && val.isNew) {
      const raw = val.query.replace(/[^a-zA-Z0-9\s]/g, '').trim();
      try {
        const res = await UserTrafficSourceAuditService.createOrGetProfession({ professionName: raw, isActive: true, createdBy: 0 });
        const newId = parseInt(res.message || '0');
        const newP: ProfessionModel = { id: newId, professionName: raw, isActive: true };
        setProfessions(prev => [...prev, newP]);
        setProfValue(newP);
        setResolvedProfId(newId);
        toast.current?.show({ severity: 'success', summary: 'Profession Created', detail: `"${raw}" added` });
      } catch {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to create profession' });
      }
    } else if (val && val.id) {
      setProfValue(val);
      setResolvedProfId(val.id);
    } else {
      setProfValue(e.value);
    }
  };

  // ── Traffic Source autocomplete ─────────────────────────────────────────
  const searchTs = (e: AutoCompleteCompleteEvent) => {
    const cleanQuery = e.query.replace(/[^a-zA-Z0-9\s]/g, '');
    const q = cleanQuery.toLowerCase().trim();
    if (!q) {
       setFilteredTs([...trafficSources]);
       return;
    }

    const matches = trafficSources.filter(t => t.trafficSourceName.toLowerCase().includes(q));
    const isExactMatch = trafficSources.some(t => t.trafficSourceName.toLowerCase() === q);
    
    if (!isExactMatch) {
       setFilteredTs([...matches, { id: -1, trafficSourceName: `+ Add "${cleanQuery}"`, isNew: true, query: cleanQuery }]);
    } else {
       setFilteredTs(matches);
    }
  };

  const onTsSelect = async (e: any) => {
    const val = e.value;
    if (val && val.isNew) {
      const raw = val.query.replace(/[^a-zA-Z0-9\s]/g, '').trim();
      try {
        const res = await UserTrafficSourceAuditService.createOrGetTrafficSource({ trafficSourceName: raw, createdBy: 0 });
        const newId = parseInt(res.message || '0');
        const newTs: TrafficSourceModel = { id: newId, trafficSourceName: raw };
        setTrafficSources(prev => [...prev, newTs]);
        setTsValue(newTs);
        setResolvedTsId(newId);
        toast.current?.show({ severity: 'success', summary: 'Source Created', detail: `"${raw}" added` });
      } catch {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to create source' });
      }
    } else if (val && val.id) {
      setTsValue(val);
      setResolvedTsId(val.id);
    } else {
      setTsValue(e.value);
    }
  };

  // ── Save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!customerId) {
      toast.current?.show({ severity: 'warn', summary: 'Required', detail: 'Please select a valid customer' }); return;
    }
    if (!resolvedProfId) {
      toast.current?.show({ severity: 'warn', summary: 'Required', detail: 'Please select or enter a profession' }); return;
    }
    if (!resolvedTsId) {
      toast.current?.show({ severity: 'warn', summary: 'Required', detail: 'Please select or enter How They Came' }); return;
    }
    if (isIntroDone && !adminUserId) {
      toast.current?.show({ severity: 'warn', summary: 'Required', detail: 'Please select who did the introduction' }); return;
    }

    setSaving(true);
    try {
      const payload: UserTrafficSourceAuditCreateModel = {
        userId:            customerId,
        professionId:      resolvedProfId,
        trafficSourceId:   resolvedTsId,
        isIntroductionDone: isIntroDone,
        introductionDoneBy: isIntroDone ? (adminUserId ?? undefined) : undefined,
        specificIssue:     specificIssue || undefined,
        createdBy:         0
      };
      await UserTrafficSourceAuditService.create(payload);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Record created successfully' });
      resetForm();
      onSaved();
      onHide();
      window.location.reload();
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to create record' });
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="flex gap-2 justify-content-end">
      <Button label="Cancel" icon="pi pi-times" className="p-button-text text-color-secondary" onClick={handleHide} />
      <Button label="Save"   icon="pi pi-check" className="p-button-success" loading={saving} onClick={handleSave} />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-user-plus text-green-500 text-xl" />
            <span className="font-bold text-lg">Add New Audit Entry</span>
          </div>
        }
        visible={visible}
        onHide={handleHide}
        footer={footer}
        modal
        style={{ width: '550px', borderRadius: '16px', overflow: 'hidden' }}
        breakpoints={{ '960px': '75vw', '640px': '95vw' }}
        contentClassName="pt-3"
      >
        {loading ? (
          <div className="text-center p-5 text-color-secondary flex flex-column align-items-center gap-3">
            <i className="pi pi-spin pi-spinner text-3xl text-green-500" />
            <span>Loading master data...</span>
          </div>
        ) : (
          <div className="formgrid grid p-fluid">
            
            {/* 1. Customer AutoComplete */}
            <div className="field col-12 mb-4">
              <label htmlFor="customer" className="font-semibold block mb-2 text-sm text-700">
                Customer <span className="text-red-500">*</span>
              </label>
              <div className="p-input-icon-left w-full">
                <i className="pi pi-user" />
                <AutoComplete
                    id="customer"
                    value={customerValue}
                    suggestions={filteredCustomers}
                    completeMethod={searchCustomer}
                    field="detail"
                    onChange={(e) => {
                        setCustomerValue(e.value);
                        if (!e.value || (typeof e.value === 'string' && e.value.trim() === '')) {
                            setCustomerId(null);
                        }
                    }}
                    onSelect={onCustomerSelect}
                    placeholder="Search by name or contact..."
                    className="w-full"
                    inputClassName="w-full pl-5"
                    minLength={1}
                    dropdown
                />
              </div>
            </div>

            {/* 2. Profession & 3. How They Came (Row) */}
            <div className="col-12 flex flex-column md:flex-row gap-3 mb-2">
              <div className="field flex-1">
                <label htmlFor="profession" className="font-semibold block mb-2 text-sm text-700">
                  Profession <span className="text-red-500">*</span>
                </label>
                <div className="p-input-icon-left w-full">
                  <i className="pi pi-briefcase" />
                   <AutoComplete
                    id="profession"
                    value={profValue}
                    suggestions={filteredProf}
                    completeMethod={searchProf}
                    field="professionName"
                    onSelect={onProfSelect}
                    onChange={(e) => {
                      const val = e.value;
                      if (typeof val === 'string') {
                        const clean = val.replace(/[^a-zA-Z0-9\s]/g, '');
                        setProfValue(clean);
                        setResolvedProfId(null);
                      } else {
                        setProfValue(val);
                      }
                    }}
                    onKeyPress={handleAlphanumericKeyPress}
                    placeholder="Search or add new..."
                    className="w-full"
                    inputClassName="w-full pl-5"
                    forceSelection={false}
                    dropdown
                  />
                </div>
              </div>

              <div className="field flex-1">
                <label htmlFor="trafficSource" className="font-semibold block mb-2 text-sm text-700">
                  How They Came <span className="text-red-500">*</span>
                </label>
                <div className="p-input-icon-left w-full">
                  <i className="pi pi-directions" />
                  <AutoComplete
                    id="trafficSource"
                    value={tsValue}
                    suggestions={filteredTs}
                    completeMethod={searchTs}
                    field="trafficSourceName"
                    onSelect={onTsSelect}
                    onChange={(e) => {
                      const val = e.value;
                      if (typeof val === 'string') {
                        const clean = val.replace(/[^a-zA-Z0-9\s]/g, '');
                        setTsValue(clean);
                        setResolvedTsId(null);
                      } else {
                        setTsValue(val);
                      }
                    }}
                    onKeyPress={handleAlphanumericKeyPress}
                    placeholder="e.g. WhatsApp, FB..."
                    className="w-full"
                    inputClassName="w-full pl-5"
                    forceSelection={false}
                    dropdown
                  />
                </div>
              </div>
            </div>

            {/* 4. Introduction Done & 5. Done By (Row) */}
            <div className="col-12 flex flex-column md:flex-row gap-3 mb-2">
              <div className="field flex-1">
                <label className="font-semibold block mb-2 text-sm text-700">
                  Introduction Done?
                </label>
                <div className="p-input-icon-left w-full">
                  <i className="pi pi-check-circle" style={{ zIndex: 1 }} />
                  <Dropdown
                    value={isIntroDone}
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false }
                    ]}
                    onChange={(e) => {
                        setIsIntroDone(e.value);
                        if (!e.value) setAdminUserId(null); 
                    }}
                    className="w-full pl-4"
                  />
                </div>
              </div>

              <div className="field flex-1" style={{ visibility: isIntroDone ? 'visible' : 'hidden' }}>
                <label htmlFor="introBy" className="font-semibold block mb-2 text-sm text-700">
                  Done By Whom? <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  id="introBy"
                  value={adminUserId}
                  options={adminUsers}
                  onChange={(e) => setAdminUserId(e.value)}
                  optionLabel="userName"
                  optionValue="id"
                  placeholder="Select admin"
                  filter
                  filterPlaceholder="Search admin..."
                  showClear
                  className="w-full"
                />
              </div>
            </div>

            {/* 6. Specific Issue */}
            <div className="field col-12 mb-0">
              <label htmlFor="specificIssue" className="font-semibold block mb-2 text-sm text-700">
                Specific Issue
              </label>
              <div className="p-input-icon-left w-full">
                <i className="pi pi-align-left" style={{ marginTop: '-1.5rem' }} />
                <InputTextarea
                  id="specificIssue"
                  value={specificIssue}
                  onChange={(e) => setSpecificIssue(e.target.value)}
                  rows={3}
                  className="w-full pl-5"
                  placeholder="Enter any specific issue here..."
                  autoResize
                />
              </div>
            </div>

          </div>
        )}
      </Dialog>
    </>
  );
};

export default UserTrafficSourceAuditAddDialog;
